import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  type Column,
  EmptyState,
  Icon,
  Pill,
  SearchInput,
  Table,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useClients } from "@/hooks/useClients";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getInitials } from "@/utils/initials";
import type { Organization } from "@/types/organization";
import { OrgCreateModal } from "./OrgCreateModal";
import "./OrganizationsList.css";

type View = "table" | "grid";

export function OrganizationsList({
  workspaceId,
  hideCreate = false,
}: {
  workspaceId: number;
  hideCreate?: boolean;
}) {
  const navigate = useNavigate();
  const { data: orgs = [], isLoading } = useOrganizations(workspaceId);
  const { data: clients = [] } = useClients(workspaceId);
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("table");
  const [creating, setCreating] = useState(false);

  // Empresa counts per organization (total + active).
  const counts = useMemo(() => {
    const m: Record<number, { total: number; ativas: number }> = {};
    for (const c of clients) {
      const e = (m[c.organization_id] ??= { total: 0, ativas: 0 });
      e.total += 1;
      if (c.active) e.ativas += 1;
    }
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) =>
        o.legal_name.toLowerCase().includes(q) ||
        (o.nickname ?? "").toLowerCase().includes(q) ||
        o.cnpj_masked.includes(q),
    );
  }, [orgs, search]);

  const columns: Column<Organization>[] = [
    {
      id: "name",
      header: "Organização",
      cell: (o) => {
        const c = counts[o.id] ?? { total: 0, ativas: 0 };
        return (
          <div className="emp-table-name">
            <span className="org-mono-mark">{getInitials(o.nickname ?? o.legal_name)}</span>
            <div>
              <div className="razao">{o.nickname ?? o.legal_name}</div>
              <div className="fantasia">
                {c.total} {c.total === 1 ? "empresa" : "empresas"} · {c.ativas}/{c.total} ativas
              </div>
            </div>
          </div>
        );
      },
    },
    { id: "cnpj", header: "CNPJ", cell: (o) => <span className="cnpj">{o.cnpj_masked}</span> },
    {
      id: "empresas",
      header: "Empresas",
      cell: (o) => {
        const c = counts[o.id] ?? { total: 0, ativas: 0 };
        return (
          <span className="col-num">
            {c.ativas}/{c.total}
          </span>
        );
      },
    },
    {
      id: "tpf",
      header: "Rec. terceiros",
      cell: (o) =>
        o.third_party_funds ? <Pill tone="diag">Sim</Pill> : <span className="td-small">—</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (o) => (
        <Pill tone={o.active ? "ativa" : "pausada"}>{o.active ? "Ativa" : "Inativa"}</Pill>
      ),
    },
  ];

  return (
    <>
      <div className="emp-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar organização…" />
        <div className="emp-toolbar-spacer" />
        <div className="view-toggle">
          <button
            className={view === "table" ? "active" : ""}
            onClick={() => setView("table")}
            aria-label="Tabela"
          >
            <Icon name="list" size={15} />
          </button>
          <button
            className={view === "grid" ? "active" : ""}
            onClick={() => setView("grid")}
            aria-label="Grade"
          >
            <Icon name="grid" size={15} />
          </button>
        </div>
        {!hideCreate && (
          <Guard permission="organizations.write">
            <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
              Nova organização
            </Button>
          </Guard>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="layers"
          size="lg"
          title={isLoading ? "Carregando…" : "Nenhuma organização"}
          description={isLoading ? undefined : "Cadastre a primeira organização do workspace."}
        />
      ) : view === "table" ? (
        <Table
          rows={filtered}
          columns={columns}
          getKey={(o) => String(o.id)}
          onRowClick={(o) => navigate(`/organizacoes/${o.id}`)}
          rowAction={() => (
            <span className="row-act" aria-hidden>
              <Icon name="more" size={16} />
            </span>
          )}
        />
      ) : (
        <div className="org-grid">
          {filtered.map((o) => {
            const c = counts[o.id] ?? { total: 0, ativas: 0 };
            return (
              <div
                key={o.id}
                className="org-card"
                onClick={() => navigate(`/organizacoes/${o.id}`)}
              >
                <div className="org-card-head">
                  <span className="org-mono-mark lg">
                    {getInitials(o.nickname ?? o.legal_name)}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="org-card-name">{o.nickname ?? o.legal_name}</div>
                    <div className="org-card-tipo">{o.cnpj_masked}</div>
                  </div>
                </div>
                <div className="org-card-stats">
                  <div className="org-stat">
                    <div className="org-stat-v">{c.total}</div>
                    <div className="org-stat-l">Empresas</div>
                  </div>
                  <div className="org-stat">
                    <div className="org-stat-v">{c.ativas}</div>
                    <div className="org-stat-l">Ativas</div>
                  </div>
                </div>
                <div className="org-card-foot">
                  <Pill tone={o.active ? "ativa" : "pausada"}>
                    {o.active ? "Ativa" : "Inativa"}
                  </Pill>
                  {o.third_party_funds && <span className="emp-card-uf">Rec. terceiros</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <OrgCreateModal workspaceId={workspaceId} onClose={() => setCreating(false)} />
      )}
    </>
  );
}
